import type { Asset } from 'contentful'
import styled, { css } from 'styled-components'

import { modularScale } from 'bl-common/src/constants/sizes'
import { Type } from 'bl-common/src/elements/Typography/Typography'
import { theme } from 'bl-common/src/styles/theme'
import { Section } from 'bl-common/src/units/Section/Section'
import { SmartImage } from 'bl-common/src/units/SmartImage/SmartImage'
import { between } from 'bl-common/src/utils/between'
import { media, mediaMax } from 'bl-common/src/utils/media'

import { BackToStoriesLink } from './BackToStoriesLink'

const StoriesHeaderContainer = styled.div`
  display: flex;

  ${mediaMax.md(css`
    max-width: 300px;
  `)}
`

const StoriesHeaderContent = styled.div`
  ${media.md(css`
    flex: 0 1 50%;
    max-width: 482px;
    margin-top: ${theme.spacing[4]};
  `)}
`

const StoriesHeaderImageContainer = styled.div`
  display: none;

  flex: 1 1 auto;
  justify-content: flex-end;
  ${media.md(css`
    display: flex;
  `)}
`
const StoriesHeaderImage = styled.div<{
  image?: string
  size?: 'large' | 'small'
}>`
  &:nth-child(1) {
    margin-right: ${theme.spacing[2.5]};
  }

  ${({ image }) =>
    image &&
    css`
      background-image: url(${image});
      background-size: cover;
      background-position: center;
    `}

  ${({ size }) =>
    size === 'large'
      ? css`
          padding-top: 43%;
          transform: translateY(40px);
          width: ${between(335 / modularScale, 335)};
          width: 46.5%;
        `
      : css`
          width: ${between(272 / modularScale, 272)};
          width: 37.4%;
          padding-top: 68%;
        `}
`

type HeaderSectionProps = {
  heading: string
  subtitle?: string
  showBreadcrumb?: boolean
  headerImages?: Asset[]
}

export const HeaderSection = ({
  heading,
  subtitle,
  showBreadcrumb,
  headerImages = [],
}: HeaderSectionProps) => {
  return (
    <Section top={{ xs: 1 }} bottom={{ xs: 4, md: 12 }} as="header">
      {showBreadcrumb && (
        <BackToStoriesLink top={{ xs: 2.5 }} bottom={{ xs: 0 }} />
      )}
      <StoriesHeaderContainer>
        <StoriesHeaderContent>
          <Type
            preset="headlineExtraLarge"
            as="h1"
            case="capitalize"
            bottom={{ xs: 1 }}
            dangerouslySetInnerHTML={{
              __html: heading,
            }}
          />
          {subtitle && (
            <Type preset="subtitle" as="h2" multiline>
              {subtitle}
            </Type>
          )}
        </StoriesHeaderContent>
        {headerImages.length === 2 && (
          <StoriesHeaderImageContainer>
            <SmartImage image={headerImages[0]}>
              {(image: string) => (
                <StoriesHeaderImage image={image} size="small" />
              )}
            </SmartImage>
            <SmartImage image={headerImages[1]}>
              {(image: string) => (
                <StoriesHeaderImage image={image} size="large" />
              )}
            </SmartImage>
          </StoriesHeaderImageContainer>
        )}
      </StoriesHeaderContainer>
    </Section>
  )
}
export default HeaderSection
